/**
 * Rotation Utility Functions
 *
 * Pure math for 3D rotation to 2D projection.
 * Used by extraction utilities and other shared modules.
 *
 * Module format: CommonJS
 */

/**
 * Apply 3D rotations to a direction vector and project to XZ plane.
 * Rotations applied in order: X → Y → Z.
 *
 * @param {Object} rotation - 3D rotation {x, y, z} in radians
 * @param {number[]} initialDirection - Starting direction vector [x, y, z], defaults to [1, 0, 0]
 * @returns {number[]} Normalized 2D direction [x, z] in the XZ plane
 */
function apply3DRotationTo2D(rotation, initialDirection = [1, 0, 0]) {
  const rx = rotation?.x || 0;
  const ry = rotation?.y || 0;
  const rz = rotation?.z || 0;

  let dirX = initialDirection[0];
  let dirY = initialDirection[1];
  let dirZ = initialDirection[2];

  // Apply X rotation (around X axis)
  const cosX = Math.cos(rx);
  const sinX = Math.sin(rx);
  let tempY = dirY * cosX - dirZ * sinX;
  let tempZ = dirY * sinX + dirZ * cosX;
  dirY = tempY;
  dirZ = tempZ;

  // Apply Y rotation (around Y axis)
  const cosY = Math.cos(ry);
  const sinY = Math.sin(ry);
  let tempX = dirX * cosY + dirZ * sinY;
  tempZ = -dirX * sinY + dirZ * cosY;
  dirX = tempX;
  dirZ = tempZ;

  // Apply Z rotation (around Z axis)
  const cosZ = Math.cos(rz);
  const sinZ = Math.sin(rz);
  tempX = dirX * cosZ - dirY * sinZ;
  tempY = dirX * sinZ + dirY * cosZ;
  dirX = tempX;
  dirY = tempY;

  // Project to XZ plane and normalize
  const dirLength = Math.sqrt(dirX * dirX + dirZ * dirZ);
  if (dirLength > 0) {
    return [dirX / dirLength, dirZ / dirLength];
  }
  return [1, 0];
}

/**
 * Apply 3D Euler rotations (X → Y → Z) to a direction vector,
 * returning the full 3D result (no projection).
 *
 * @param {Object} rotation - 3D rotation {x, y, z} in radians
 * @param {number[]} initialDirection - Starting direction vector [x, y, z], defaults to [1, 0, 0]
 * @returns {Object} Rotated direction vector {x, y, z}
 */
function applyRotation3D(rotation, initialDirection = [1, 0, 0]) {
  const rx = rotation?.x || 0;
  const ry = rotation?.y || 0;
  const rz = rotation?.z || 0;

  let dirX = initialDirection[0];
  let dirY = initialDirection[1];
  let dirZ = initialDirection[2];

  // Apply X rotation (around X axis)
  const cosX = Math.cos(rx);
  const sinX = Math.sin(rx);
  let tempY = dirY * cosX - dirZ * sinX;
  let tempZ = dirY * sinX + dirZ * cosX;
  dirY = tempY;
  dirZ = tempZ;

  // Apply Y rotation (around Y axis)
  const cosY = Math.cos(ry);
  const sinY = Math.sin(ry);
  let tempX = dirX * cosY + dirZ * sinY;
  tempZ = -dirX * sinY + dirZ * cosY;
  dirX = tempX;
  dirZ = tempZ;

  // Apply Z rotation (around Z axis)
  const cosZ = Math.cos(rz);
  const sinZ = Math.sin(rz);
  tempX = dirX * cosZ - dirY * sinZ;
  tempY = dirX * sinZ + dirY * cosZ;
  dirX = tempX;
  dirY = tempY;

  return { x: dirX, y: dirY, z: dirZ };
}

module.exports = {
  apply3DRotationTo2D,
  applyRotation3D,
};
